/* MANISH CUSTOM CODE - FAST DELIVERY PINCODE CHECK (manish-cc) - START
 *
 * Powers blocks/manish-cc-delivery-check.liquid:
 *   - checks a typed pincode against the lists set in the theme editor
 *   - shows Regular / Fast / Same-day options (with the fee read from the
 *     "Fast Delivery Fee" product) and the hours rule
 *   - when Fast is chosen, switches on three hidden cart-line values
 *     (Delivery, Delivery pincode, _delivery_tier) that belong to the product
 *     form via the form="" attribute
 *   - remembers the last pincode in this browser (if storage is allowed)
 *
 * Adding the Fast Delivery Fee line to the cart is NOT done here; that is
 * assets/manish-cc-delivery-guard.js (loaded on every page).
 */

const STORE_KEY = 'mm-delivery-pincode';
const TIMEZONE = 'Asia/Kolkata';

export function parsePins(text) {
  const set = new Set();
  if (!text) return set;
  String(text)
    .split(/[^0-9]+/)
    .forEach((p) => {
      if (/^[0-9]{6}$/.test(p)) set.add(p);
    });
  return set;
}

export function toMinutes(hhmm, fallback) {
  const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*$/.exec(String(hhmm || ''));
  if (!m) return fallback;
  const h = Number(m[1]);
  const min = Number(m[2] || 0);
  if (h > 24 || min > 59) return fallback;
  return h * 60 + min;
}

function nowMinutesIST(now) {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now || new Date());
    const h = Number(parts.find((p) => p.type === 'hour').value) % 24;
    const m = Number(parts.find((p) => p.type === 'minute').value);
    return h * 60 + m;
  } catch (e) {
    // Fallback: fixed +05:30 offset from UTC
    const d = now || new Date();
    return (d.getUTCHours() * 60 + d.getUTCMinutes() + 330) % 1440;
  }
}

/**
 * Works out what a pincode can get.
 * A pincode in the 30-minute list gets BOTH 30-minute and same-day delivery; a pincode only in
 * the same-day list gets same-day. Each tier carries its own time rule (timeOk).
 * @returns {{status: 'invalid'|'blocked'|'regular'|'fast', tiers?: {p30?: {timeOk: boolean}, sd?: {timeOk: boolean}}}}
 */
export function classify(pin, cfg, now) {
  if (!/^[0-9]{6}$/.test(pin)) return { status: 'invalid' };
  if (parsePins(cfg.blocked).has(pin)) return { status: 'blocked' };

  const nowMin = nowMinutesIST(now);
  const inP30 = parsePins(cfg.p30).has(pin);
  const inSd = inP30 || parsePins(cfg.sd).has(pin);
  if (!inP30 && !inSd) return { status: 'regular' };

  const tiers = {};
  if (inP30) {
    const start = toMinutes(cfg.p30Start, 600);
    const end = toMinutes(cfg.p30End, 1200);
    tiers.p30 = { timeOk: nowMin >= start && nowMin < end };
  }
  if (inSd) {
    const cutoff = toMinutes(cfg.sdCutoff, 900);
    tiers.sd = { timeOk: nowMin < cutoff };
  }
  return { status: 'fast', tiers };
}

function fmtTime(hhmm) {
  const mins = toMinutes(hhmm, null);
  if (mins === null) return String(hhmm);
  const h24 = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const suffix = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return h12 + (m ? ':' + String(m).padStart(2, '0') : '') + ' ' + suffix;
}

/**
 * The product form that belongs to a delivery box: the nearest ancestor of the box that
 * contains one. Found by position (not by id) so it also works inside the quick-add popup.
 */
function formFor(root) {
  for (let el = root.parentElement; el; el = el.parentElement) {
    const f = el.querySelector('product-form-component');
    if (f) return f;
  }
  return null;
}

function initBox(root) {
  if (root.dataset.mmDlvReady) return;
  root.dataset.mmDlvReady = '1';

  let cfg;
  try {
    cfg = JSON.parse(root.querySelector('[data-mm-dlv-config]').textContent);
  } catch (e) {
    return;
  }

  if (cfg.mode === 'simple') {
    initSimple(root, cfg);
    return;
  }

  const input = root.querySelector('[data-mm-dlv-pin]');
  const checkBtn = root.querySelector('[data-mm-dlv-check]');
  const msg = root.querySelector('[data-mm-dlv-msg]');
  const opts = root.querySelector('[data-mm-dlv-opts]');
  const tierOpts = {
    p30: root.querySelector('[data-mm-dlv-opt="p30"]'),
    sd: root.querySelector('[data-mm-dlv-opt="sd"]'),
  };
  const prepaid = root.querySelector('[data-mm-dlv-prepaid]');
  const radios = Array.from(root.querySelectorAll('[data-mm-dlv-radio]'));
  const props = {
    label: root.querySelector('[data-mm-dlv-prop="label"]'),
    pin: root.querySelector('[data-mm-dlv-prop="pin"]'),
    tier: root.querySelector('[data-mm-dlv-prop="tier"]'),
  };
  const findProductForm = () => formFor(root);

  // The three hidden values only hold data. They must never be submitted by themselves
  // (the formdata listener below adds them to the request when Fast is picked).
  Object.values(props).forEach((p) => {
    p.removeAttribute('form');
    p.disabled = true;
  });

  let current = null; // last successful classification for the typed pincode
  let currentPin = '';

  function say(text, kind) {
    msg.textContent = text || '';
    msg.classList.toggle('mm-dlv__msg--error', kind === 'error');
    msg.classList.toggle('mm-dlv__msg--info', kind === 'info');
  }

  // tier: 'p30', 'sd' or null (regular delivery)
  function setFast(tier) {
    // Fast is only switched on when the cart guard (which adds the fee line) is running.
    const ok = !!(tier && current && current.tiers && current.tiers[tier]);
    const enable = !!(ok && cfg.fee && cfg.fee[tier] && window.__mmDeliveryGuard);
    const productForm = findProductForm();
    if (enable) {
      props.label.value = tier === 'p30' ? cfg.text.p30Label : cfg.text.sdLabel;
      props.pin.value = currentPin;
      props.tier.value = tier;
      // The add-to-cart request reads these (see the formdata listener below).
      root.dataset.mmTier = tier;
      productForm && productForm.setAttribute('data-mm-fast', '1');
    } else {
      props.label.value = props.pin.value = props.tier.value = '';
      delete root.dataset.mmTier;
      productForm && productForm.removeAttribute('data-mm-fast');
    }
    if (prepaid) prepaid.hidden = !enable;
  }

  function reset() {
    current = null;
    opts.hidden = true;
    Object.values(tierOpts).forEach((o) => {
      if (o) o.hidden = true;
    });
    radios.forEach((r) => {
      r.checked = r.value === 'regular';
    });
    setFast(null);
  }

  function run(pin, fromUser) {
    const result = classify(pin, cfg);
    reset();

    if (result.status === 'invalid') {
      say(fromUser ? cfg.text.invalid : '', 'error');
      return;
    }

    currentPin = pin;
    current = result;

    if (result.status === 'blocked') {
      say(cfg.text.blocked, 'error');
      return;
    }

    // (2026-10-19) A pincode that is not on the 30-minute / same-day lists only gets the
    // message - the delivery option cards are shown for fast-delivery pincodes only.
    if (result.status === 'regular') {
      say(cfg.text.okRegular + ' ' + cfg.text.notServedFast, 'info');
      return;
    }

    const notes = [];
    let shown = 0;
    ['p30', 'sd'].forEach((tier) => {
      const info = result.tiers[tier];
      const opt = tierOpts[tier];
      const feeText = cfg.fee && cfg.fee[tier];
      // A tier is offered only if the pincode qualifies AND its fee variant is available.
      if (!info || !opt || !feeText) return;
      shown += 1;
      opt.hidden = false;
      opt.querySelector('[data-mm-dlv-fee="' + tier + '"]').textContent = '+ ' + feeText;
      const radio = opt.querySelector('[data-mm-dlv-radio]');
      radio.disabled = !info.timeOk;
      opt.classList.toggle('mm-dlv__opt--disabled', !info.timeOk);
      if (!info.timeOk) {
        notes.push(
          tier === 'p30'
            ? cfg.text.outsideP30.replace('[start]', fmtTime(cfg.p30Start)).replace('[end]', fmtTime(cfg.p30End))
            : cfg.text.outsideSd.replace('[cutoff]', fmtTime(cfg.sdCutoff))
        );
      }
    });

    if (!shown) {
      // The Fast Delivery Fee product is not Active/available: message only, no option cards.
      say(cfg.text.okRegular, 'info');
      return;
    }
    opts.hidden = false;
    say(notes.length ? notes.join(' ') : cfg.text.okRegular, notes.length ? 'info' : '');
  }

  function save(pin) {
    try {
      localStorage.setItem(STORE_KEY, pin);
    } catch (e) {
      /* storage may be blocked; the box still works */
    }
  }

  checkBtn.addEventListener('click', () => {
    const pin = input.value.trim();
    run(pin, true);
    if (/^[0-9]{6}$/.test(pin)) save(pin);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // never submit the product form from this box
      checkBtn.click();
    }
  });

  input.addEventListener('input', () => {
    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 6);
    // Changing the pincode drops any earlier Fast choice until it is checked again.
    if (current) {
      reset();
      say('', '');
    }
  });

  radios.forEach((r) => {
    r.addEventListener('change', () => {
      if (r.checked) setFast(r.value === 'p30' || r.value === 'sd' ? r.value : null);
    });
  });

  // Start with the last pincode this visitor checked, if any.
  try {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved && /^[0-9]{6}$/.test(saved)) {
      input.value = saved;
      run(saved, false);
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * SIMPLE PINCODE CHECK (blocks/manish-cc-delivery-check.liquid, "Simple pincode check"):
 * same look as the working box, but any valid 6-digit pincode just gets one fixed message.
 * No delivery options and nothing is added to the cart.
 */
function initSimple(root, cfg) {
  const input = root.querySelector('[data-mm-dlv-pin]');
  const checkBtn = root.querySelector('[data-mm-dlv-check]');
  const msg = root.querySelector('[data-mm-dlv-msg]');
  const text = cfg.text || {};

  const range = deliveryRange(cfg.daysMin, cfg.daysMax);

  function show(pin, fromUser) {
    if (!/^[0-9]{6}$/.test(pin)) {
      msg.textContent = fromUser ? text.invalid || '' : '';
      msg.classList.add('mm-dlv__msg--error');
      return false;
    }
    msg.classList.remove('mm-dlv__msg--error');
    // Built from text nodes (never HTML), so whatever is typed in the theme editor is safe.
    // [from] and [to] are shown in bold.
    msg.textContent = '';
    const values = { pincode: pin, from: range.from, to: range.to };
    String(text.ok || '')
      .split(/(\[(?:pincode|from|to)\])/g)
      .forEach((part) => {
        const key = /^\[(pincode|from|to)\]$/.exec(part);
        if (key && key[1] !== 'pincode') {
          const b = document.createElement('strong');
          b.textContent = values[key[1]];
          msg.appendChild(b);
        } else if (part) {
          msg.appendChild(document.createTextNode(key ? values.pincode : part));
        }
      });
    return true;
  }

  checkBtn.addEventListener('click', () => {
    const pin = input.value.trim();
    if (show(pin, true)) {
      try {
        localStorage.setItem(STORE_KEY, pin);
      } catch (e) {
        /* storage may be blocked */
      }
    }
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // never submit the product form from this box
      checkBtn.click();
    }
  });

  input.addEventListener('input', () => {
    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 6);
    msg.textContent = '';
  });

  try {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved && /^[0-9]{6}$/.test(saved)) {
      input.value = saved;
      show(saved, false);
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * Delivery dates for the simple box: today (India time) + the two day counts from the block
 * settings, written like "Wed 28 Oct". Calendar days, Sundays included.
 */
export function deliveryRange(minDays, maxDays, now) {
  let lo = Number.isFinite(Number(minDays)) ? Number(minDays) : 9;
  let hi = Number.isFinite(Number(maxDays)) ? Number(maxDays) : 14;
  if (hi < lo) [lo, hi] = [hi, lo];
  let y, m, d;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: TIMEZONE, year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now || new Date());
    y = Number(parts.find((p) => p.type === 'year').value);
    m = Number(parts.find((p) => p.type === 'month').value);
    d = Number(parts.find((p) => p.type === 'day').value);
  } catch (e) {
    const t = new Date((now || new Date()).getTime() + 330 * 60000);
    y = t.getUTCFullYear();
    m = t.getUTCMonth() + 1;
    d = t.getUTCDate();
  }
  const fmt = (add) => {
    const dt = new Date(Date.UTC(y, m - 1, d + add));
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(dt).replace(',', '');
    } catch (e) {
      return dt.toUTCString().slice(0, 11);
    }
  };
  return { from: fmt(lo), to: fmt(hi) };
}

function boot() {
  document.querySelectorAll('[data-mm-delivery]').forEach(initBox);
}

/**
 * Adds the Fast delivery values to an add-to-cart request. The browser fires "formdata" on a
 * form whenever its values are collected, so this works for the product page form and for the
 * form inside the quick-add popup alike. Only the box that belongs to the same product is used.
 */
function onFormData(event) {
  const form = event.target;
  if (!form || form.tagName !== 'FORM' || !event.formData) return;
  const holder = form.closest('product-form-component');
  if (!holder) return;
  for (let el = form.parentElement; el; el = el.parentElement) {
    const box = el.querySelector('[data-mm-delivery]');
    if (!box) continue;
    // Only the box that belongs to this very form counts (a card's quick-add form on the same
    // page must not pick up the product page's choice).
    if (formFor(box) !== holder) return;
    if (!box.dataset.mmTier) return;
    const get = (k) => box.querySelector('[data-mm-dlv-prop="' + k + '"]').value;
    event.formData.set('properties[Delivery]', get('label'));
    event.formData.set('properties[Delivery pincode]', get('pin'));
    event.formData.set('properties[_delivery_tier]', get('tier'));
    return;
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor / section re-renders
  document.addEventListener('shopify:section:load', boot);

  document.addEventListener('formdata', onFormData);

  // Boxes that appear later (the quick-add popup loads a product page into the page).
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((records) => {
      for (const r of records) {
        for (const n of r.addedNodes) {
          if (n.nodeType === 1 && (n.matches('[data-mm-delivery]') || n.querySelector('[data-mm-delivery]'))) {
            boot();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
}

/* MANISH CUSTOM CODE - FAST DELIVERY PINCODE CHECK (manish-cc) - END */
